import {
  CustomerHeaderCustomersByIdFragment,
  HeadAgreementForMultilegFragment,
  RouteAgreementsForMultilegRouteFragment,
} from "@/gql/graphql";
import { Box } from "@stenajs-webui/core";
import {
  BreadCrumbs,
  ResultListBanner,
  ResultListBannerState,
  stenaBusinessAgreement,
} from "@stenajs-webui/elements";
import { uniq } from "lodash";
import * as React from "react";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { areObjectsEqual } from "@/lib/compare/ObjectEqual";
import { Crumb } from "@/components/bread-crumbs/Crumb";
import { MultilegRoutePairSelect } from "@/components/data-driven-inputs/selects/multileg-route-pair-select/MultilegRoutePairSelect";
import { SwitchWithLabelForMultilegSalesReady } from "@/components/data-driven-inputs/switches/SwitchWithLabelForMultilegSalesReady";
import { PageHeader } from "@/components/page-header/PageHeader";
import { StoreState } from "@/redux/RootReducer";
import { routeFactory } from "../../../../RouteFactory";
import { customerMultiLegRouteRedux } from "../redux";
import { CustomerMultilegRouteSaveButton } from "./CustomerMultilegRouteSaveButton";

interface Props {
  customer: CustomerHeaderCustomersByIdFragment;
  routePairId: string;
  headAgreement: HeadAgreementForMultilegFragment;
  multilegAgreementForRoutePair: RouteAgreementsForMultilegRouteFragment;
  loadingCustomer: boolean;
}

const errorSelector = (state: StoreState) =>
  state.customerRoute.progress.errors;

export const CustomerMultilegRouteHeader: React.FC<Props> = ({
  routePairId,
  customer,
  headAgreement,
  multilegAgreementForRoutePair,
  loadingCustomer,
}) => {
  const saveErrors = useSelector(errorSelector);
  const dispatch = useDispatch();
  const { multiLegAgreementId } = useParams() as {
    multiLegAgreementId: string;
  };

  const navigate = useNavigate();

  const customerId = customer.id;
  const headAgreementId = headAgreement.id;

  const bannerState = useMemo<ResultListBannerState | undefined>(() => {
    if (saveErrors && saveErrors.length) {
      const distinctErrorMessages = uniq(
        saveErrors.map((error) => error.message)
      );

      return {
        headerText: "Unable to save",
        items: uniq(
          distinctErrorMessages.map((e) => ({
            text: e,
          }))
        ),
      };
    }
  }, [saveErrors]);

  const multiLegAgreement = useSelector(
    (state: StoreState) => state.customerMultiLegRoute.multiLegAgreement
  );

  const setSalesReady = useCallback(
    (isSalesReady: boolean) =>
      dispatch(customerMultiLegRouteRedux.actions.setSalesReady(isSalesReady)),
    [dispatch]
  );

  const hasChanges = useMemo(
    () =>
      !areObjectsEqual(multiLegAgreement.editable, multiLegAgreement.persisted),
    [multiLegAgreement]
  );
  if (!multiLegAgreement) {
    return null;
  }

  return (
    <>
      <PageHeader
        title={"Multi leg route rates"}
        loading={loadingCustomer}
        icon={stenaBusinessAgreement}
        contentUnder={
          bannerState ? (
            <ResultListBanner bannerState={bannerState} variant={"error"} />
          ) : undefined
        }
        breadcrumbs={
          <BreadCrumbs>
            <Crumb
              label={customer?.breadCrumbName ?? ""}
              path={routeFactory.productAndPrice.customer.customer({
                customerId,
              })}
            />
            <Crumb
              label={String(headAgreement?.headAgreementNumber ?? "")}
              path={routeFactory.productAndPrice.customer.customerHeadAgreement(
                {
                  customerId,
                  headAgreementId: headAgreement?.id ?? "",
                }
              )}
            />
            <Crumb
              label={`${routePairId} (${multilegAgreementForRoutePair.idNumber})`}
              path={routeFactory.productAndPrice.multiLegAgreement.multiLegAgreementDetails(
                {
                  customerId,
                  headAgreementId,
                  multiLegAgreementId: multiLegAgreementId,
                }
              )}
            />
          </BreadCrumbs>
        }
        contentLeft={
          <Box width={"170px"}>
            <MultilegRoutePairSelect
              value={multilegAgreementForRoutePair.id}
              headAgreementId={headAgreementId}
              onValueChange={(multiLegAgreementId) =>
                navigate(
                  routeFactory.productAndPrice.multiLegAgreement.multiLegAgreementDetails(
                    {
                      customerId,
                      headAgreementId,
                      multiLegAgreementId,
                    }
                  )
                )
              }
            />
          </Box>
        }
        contentRight={
          <>
            <SwitchWithLabelForMultilegSalesReady
              multilegAgreementStatusCode={
                multilegAgreementForRoutePair.statusCode
              }
              label={"Sales ready"}
              value={multiLegAgreement.editable.isSalesReady}
              onValueChange={setSalesReady}
            />
            <CustomerMultilegRouteSaveButton
              multiLegAgreementToSave={multilegAgreementForRoutePair}
              headAgreement={headAgreement}
              routePairId={routePairId}
              canSave={hasChanges}
              customerId={customerId}
            />
          </>
        }
      />
    </>
  );
};
